import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

interface MobileCardField {
  label: string;
  value: ReactNode;
  /** Prend toute la largeur */
  fullWidth?: boolean;
}

interface MobileCardProps {
  /** Titre principal */
  title: ReactNode;
  /** Sous-titre */
  subtitle?: ReactNode;
  /** Badge de statut (coin supérieur droit) */
  status?: ReactNode;
  /** Champs affichés en grille */
  fields: MobileCardField[];
  /** Actions (boutons) */
  actions?: ReactNode;
  /** Click sur la carte */
  onClick?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

export function MobileCard({
  title,
  subtitle,
  status,
  fields,
  actions,
  onClick,
  className = '',
}: MobileCardProps) {
  return (
    <div
      onClick={onClick}
      className={`tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 tw-shadow-sm tw-transition-all ${
        onClick
          ? 'tw-cursor-pointer hover:tw-shadow-md hover:tw-border-gray-300 active:tw-scale-[0.99]'
          : ''
      } ${className}`}
    >
      {/* Header */}
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-3">
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-truncate">
            {title}
          </div>
          {subtitle && (
            <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-truncate">
              {subtitle}
            </div>
          )}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
          {status}
          {onClick && (
            <ChevronRight className="tw-w-4 tw-h-4 tw-text-gray-400" />
          )}
        </div>
      </div>

      {/* Fields grid */}
      {fields.length > 0 && (
        <div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-2">
          {fields.map((field, i) => (
            <div
              key={i}
              className={field.fullWidth ? 'tw-col-span-2' : ''}
            >
              <div className="tw-text-[10px] tw-font-medium tw-text-gray-400 tw-uppercase tw-tracking-wider">
                {field.label}
              </div>
              <div className="tw-text-sm tw-text-gray-700">{field.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      {actions && (
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-100">
          {actions}
        </div>
      )}
    </div>
  );
}
